import createCache from "@emotion/cache";
import { CacheProvider, ThemeProvider } from "@emotion/react";
import { Box, Button } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import React, { useState } from "react";
import { useQueryClient } from "react-query";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import ChipCmp from "../../../components/chip/chipCmp";
import AutocomplateCheckbox from "../../../components/dropdown/AutocomplateCheckbox";
import MaterialTextFieldComponent from "../../../components/textField/MaterialTextFieldCmp";
import { GetStatusResponse } from "../../../models/basicInformation.model";
import { LoanRequestStatus } from "../../../models/customer.model";
import { GetInProcessLoansFilter } from "../../../models/loan.model";

const theme = createTheme({
  direction: "rtl",
});

const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});
interface Props {
  setFilterObject: (filterObj: GetInProcessLoansFilter) => void;
  filterObject: GetInProcessLoansFilter;
  setCurrentPaginationInfo: (page: any) => void;
  loanTemplateOptions: GetStatusResponse[];
  organizationOptions: GetStatusResponse[];
  loanRequestStatus: GetStatusResponse[];
}

const InProcessLoansListFilters = ({
  filterObject,
  setCurrentPaginationInfo,
  setFilterObject,
  loanTemplateOptions,
  organizationOptions,
  loanRequestStatus,
}: Props) => {
  const [formData, setFormData] = useState<GetInProcessLoansFilter>();
  const [valueLoanTemplateIds, setValueLoanTemplateIds] = useState<number[]>(
    []
  );
  const [valueLoanTemplateResponse, setValueLoanTemplateResponse] = useState<
    any[]
  >([]);
  const queryClient = useQueryClient();
  const [valueOrganizationIds, setValueOrganizationIds] = useState<number[]>(
    []
  );
  const [valueOrganizationResponse, setValueOrganizationResponse] = useState<
    any[]
  >([]);

  const [loanRequestStatuses, setLoanRequestStatuses] = useState<
    LoanRequestStatus[]
  >([]);
  const [
    loanRequestStatusesResponse,
    setLoanRequestStatusesResponse,
  ] = useState<any[]>([]);

  const handleChangeForm = (event: any) => {
    setFormData({ ...formData!, [event.target.id]: event.target.value });
    queryClient.setQueryData<GetInProcessLoansFilter>(
      "GetInProcessLoansFilter",
      { ...formData!, [event.target.id]: event.target.value }
    );
  };

  const changeLoanTemplateIds = (e: any, value: any) => {
    setValueLoanTemplateResponse([...value]);
    setValueLoanTemplateIds([...e]);
    let tableFilter = queryClient.getQueryData<GetInProcessLoansFilter>(
      "GetInProcessLoansFilter"
    );
    queryClient.setQueryData<GetInProcessLoansFilter>(
      "GetInProcessLoansFilter",
      { ...tableFilter, loanTemplateIds: [...e] }
    );
  };
  const changeOrganizationIds = (e: any, value: any) => {
    setValueOrganizationResponse([...value]);
    setValueOrganizationIds([...e]);
    let tableFilter = queryClient.getQueryData<GetInProcessLoansFilter>(
      "GetInProcessLoansFilter"
    );
    queryClient.setQueryData<GetInProcessLoansFilter>(
      "GetInProcessLoansFilter",
      { ...tableFilter, organizationIds: [...e] }
    );
  };

  const changeLoanRequestStatuses = (e: any, value: any) => {
    setLoanRequestStatusesResponse([...value]);
    setLoanRequestStatuses([...e]);
    let tableFilter = queryClient.getQueryData<GetInProcessLoansFilter>(
      "GetInProcessLoansFilter"
    );
    queryClient.setQueryData<GetInProcessLoansFilter>(
      "GetInProcessLoansFilter",
      { ...tableFilter, loanRequestStatuses: [...e] }
    );
  };

  const handleChip = (e: any, id: string) => {
    let tableFilter = queryClient.getQueryData<GetInProcessLoansFilter>(
      "GetInProcessLoansFilter"
    );
    tableFilter = {
      ...tableFilter!,
      [id]: "",
    };
    setFormData({ ...formData!, [id]: "" });
    queryClient.setQueryData<GetInProcessLoansFilter>(
      "GetInProcessLoansFilter",
      tableFilter
    );
    setFilterObject(tableFilter);
  };
  const handleAutocompletesChip = (chip: any) => {
    const selectedItem = valueLoanTemplateIds.filter(
      (item: any) => item !== chip
    );
    const remaningItems = valueLoanTemplateResponse?.filter(
      (item: any) => item.id !== chip
    );
    setValueLoanTemplateIds(selectedItem);
    setValueLoanTemplateResponse(remaningItems);
    const tabelFilter = queryClient.getQueryData<GetInProcessLoansFilter>(
      "GetInProcessLoansFilter"
    );
    if (tabelFilter !== undefined && tabelFilter != null) {
      tabelFilter!.loanTemplateIds = tabelFilter!.loanTemplateIds
        ?.filter((item: any) => item !== chip)
        .map((items: any) => {
          return items;
        });
    }
    setFilterObject({
      ...filterObject,
      loanTemplateIds: tabelFilter?.loanTemplateIds,
    });
  };
  const handleOrganizationIdChip = (chip: any) => {
    const selectedItem = valueOrganizationIds.filter(
      (item: any) => item !== chip
    );
    const remainingItems = valueOrganizationResponse?.filter(
      (item: any) => item.id !== chip
    );
    setValueOrganizationIds(selectedItem);
    setValueOrganizationResponse(remainingItems);
    const tableFilter = queryClient.getQueryData<GetInProcessLoansFilter>(
      "GetInProcessLoansFilter"
    );
    if (tableFilter !== undefined && tableFilter != null) {
      tableFilter!.organizationIds = tableFilter!.organizationIds
        ?.filter((item: any) => item !== chip)
        .map((items: any) => {
          return items;
        });
    }
    setFilterObject({
      ...filterObject,
      organizationIds: tableFilter?.organizationIds,
    });
  };
  const handleLoanRequestStatuses = (chip: any) => {
    const selectedItem = loanRequestStatuses.filter(
      (item: any) => item !== chip
    );
    const remainingItems = loanRequestStatusesResponse?.filter(
      (item: any) => item.id !== chip
    );
    setLoanRequestStatuses(selectedItem);
    setLoanRequestStatusesResponse(remainingItems);
    const tableFilter = queryClient.getQueryData<GetInProcessLoansFilter>(
      "GetInProcessLoansFilter"
    );
    if (tableFilter !== undefined && tableFilter != null) {
      tableFilter!.loanRequestStatuses = tableFilter!.loanRequestStatuses
        ?.filter((item: any) => item !== chip)
        .map((items: any) => {
          return items;
        });
    }
    setFilterObject({
      ...filterObject,
      loanRequestStatuses: tableFilter?.loanRequestStatuses,
    });
  };
  const deleteChip = () => {
    let tableFilter = queryClient.getQueryData<GetInProcessLoansFilter>(
      "GetInProcessLoansFilter"
    );
    tableFilter = undefined;
    setFormData({
      mobileNumber: "",
      fullName: "",
      nationalCode: "",
    });
    setValueOrganizationIds([]);
    setValueLoanTemplateIds([]);
    setValueLoanTemplateResponse([]);
    setValueOrganizationResponse([]);
    queryClient.setQueryData<GetInProcessLoansFilter>(
      "GetInProcessLoansFilter",
      tableFilter!
    );
    setFilterObject(tableFilter!);
  };
  const handleFormSubmit = () => {
    let tableFilter = queryClient.getQueryData<GetInProcessLoansFilter>(
      "GetInProcessLoansFilter"
    );
    tableFilter = {
      ...tableFilter!,
      loanTemplateIds: valueLoanTemplateIds,
      organizationIds: valueOrganizationIds,
    };
    queryClient.setQueryData<GetInProcessLoansFilter>(
      "GetInProcessLoansFilter",
      tableFilter
    );
    setCurrentPaginationInfo({
      pageNumber: 1,
      pageSize: 10,
    });

    setFilterObject(tableFilter);
  };
  return (
    <div>
      <Box>
        <div className="row">
          <div
            className="col-lg-2 col-md-4 col-sm-6 col-xs-12 col-12"
            style={{ marginTop: "1vh" }}
          >
            <MaterialTextFieldComponent
              helperText={""}
              placeHolder={"متقاضی"}
              label={"متقاضی"}
              required={false}
              value={formData?.fullName}
              onChange={(e: any) => handleChangeForm(e)}
              name="fullName"
              id="fullName"
            />
          </div>
          <div
            className="col-lg-2 col-md-4 col-sm-6 col-xs-12 col-12"
            style={{ marginTop: "1vh" }}
          >
            <MaterialTextFieldComponent
              helperText={""}
              placeHolder={"کد ملی متقاضی"}
              label={"کد ملی متقاضی"}
              required={false}
              value={formData?.nationalCode}
              onChange={(e: any) => handleChangeForm(e)}
              name="nationalCode"
              id="nationalCode"
              onKeyDown={(event) => {
                if (
                  event.code === "Space" ||
                  (!/^[0-9]$/.test(event.key) && event.code !== "Backspace")
                )
                  event.preventDefault();
              }}
            />
          </div>
          <div
            className="col-lg-2 col-md-4 col-sm-6 col-xs-12 col-12"
            style={{ marginTop: "1vh" }}
          >
            <MaterialTextFieldComponent
              helperText={""}
              placeHolder={"شماره همراه"}
              label={"شماره همراه"}
              required={false}
              value={formData?.mobileNumber}
              onChange={(e: any) => handleChangeForm(e)}
              name="mobileNumber"
              id="mobileNumber"
              onKeyDown={(event) => {
                if (
                  event.code === "Space" ||
                  (!/^[0-9]$/.test(event.key) && event.code !== "Backspace")
                )
                  event.preventDefault();
              }}
            />
          </div>

          <div
            className="col-lg-2 col-md-4 col-sm-6 col-xs-12 col-12"
            style={{ marginTop: "1vh" }}
          >
            <CacheProvider value={cacheRtl}>
              <ThemeProvider theme={theme}>
                <div dir="rtl">
                  <AutocomplateCheckbox
                    id="loanRequestStatuses"
                    name="loanRequestStatuses"
                    value={loanRequestStatuses}
                    label="وضعیت تسهیلات"
                    options={loanRequestStatus}
                    onChange={changeLoanRequestStatuses}
                    changeMultiSelect={changeLoanRequestStatuses}
                    responseValue={loanRequestStatusesResponse}
                  />
                </div>
              </ThemeProvider>
            </CacheProvider>
          </div>
          <div
            className="col-lg-2 col-md-4 col-sm-6 col-xs-12 col-12 dropdownCmp2-container"
            style={{ marginTop: "1vh" }}
          >
            <CacheProvider value={cacheRtl}>
              <ThemeProvider theme={theme}>
                {loanTemplateOptions && (
                  <div dir="rtl">
                    <AutocomplateCheckbox
                      id="loanTemplateIds"
                      name="loanTemplateIds"
                      value={valueLoanTemplateIds}
                      label={"طرح تسهیلات"}
                      options={loanTemplateOptions}
                      onChange={changeLoanTemplateIds}
                      changeMultiSelect={changeLoanTemplateIds}
                      responseValue={valueLoanTemplateResponse}
                    />
                  </div>
                )}
              </ThemeProvider>
            </CacheProvider>
          </div>
          <div
            className="col-lg-2 col-md-4 col-sm-6 col-xs-12 col-12 dropdownCmp2-container"
            style={{ marginTop: "1vh" }}
          >
            <CacheProvider value={cacheRtl}>
              <ThemeProvider theme={theme}>
                {organizationOptions && (
                  <div dir="rtl">
                    <AutocomplateCheckbox
                      id="organizationIds"
                      name="organizationIds"
                      value={valueOrganizationIds}
                      label={"سازمان"}
                      options={organizationOptions}
                      onChange={changeOrganizationIds}
                      changeMultiSelect={changeOrganizationIds}
                      responseValue={valueOrganizationResponse}
                    />
                  </div>
                )}
              </ThemeProvider>
            </CacheProvider>
          </div>
          <div
            className="col-lg-2 col-md-4 col-sm-6 col-xs-12 col-12 butFilter"
            style={{ marginTop: "1vh" }}
          >
            <Button
              fullWidth
              variant="contained"
              className="btnForm SuccessDefault  "
              onClick={handleFormSubmit}
            >
              اعمال فیلتر
            </Button>
          </div>
        </div>

        {/*  chips */}
        {(formData?.mobileNumber ||
          formData?.fullName ||
          formData?.nationalCode ||
          valueLoanTemplateIds?.length > 0 ||
          valueOrganizationIds?.length > 0 ||
          loanRequestStatuses?.length > 0) && (
          <div
            style={{ display: "flex", alignItems: "center" }}
            className="row tableCMP"
          >
            <Button
              onClick={deleteChip}
              style={{
                justifyContent: "right",
                minWidth: "25px",
                marginRight: "-1vh",
                marginTop: "1vh",
              }}
            >
              <img
                style={{ height: "24px", width: "24px" }}
                src="images/RemoveAllFilters.svg"
                alt="remove"
              />
            </Button>
            {Object.keys(formData || {}).map((key) => (
              <ChipCmp
                key={key}
                item={(formData as any)[key]}
                enamValue={(formData as any)[key]}
                index={(formData as any)[key]?.length}
                setChildValue={(e: any) => handleChip(e, key)}
              />
            ))}
            <ChipCmp
              item={valueLoanTemplateIds}
              enamValue={loanTemplateOptions}
              index={valueLoanTemplateIds?.length}
              setChildValue={handleAutocompletesChip}
            />
            <ChipCmp
              item={valueOrganizationIds}
              enamValue={organizationOptions}
              index={valueOrganizationIds?.length}
              setChildValue={handleOrganizationIdChip}
            />
            <ChipCmp
              item={loanRequestStatuses}
              enamValue={loanRequestStatus}
              index={loanRequestStatuses?.length}
              setChildValue={handleLoanRequestStatuses}
            />
          </div>
        )}
      </Box>
    </div>
  );
};

export default InProcessLoansListFilters;
